import React, { useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Dialog,
    MenuItem,
    MenuList,
    Radio,
    RadioGroup,
} from '@biq/apex-ui';
import {
    ClickAwayListener,
    Fade,
    Grid,
    ListItemText,
    Paper,
    Typography,
} from '@mui/material';
import InvalidPermissionsDialog from '../invalid-permissions-dialog/InvalidPermissionsDialog';
import {
    CreateAccordionMenuItemsData,
    createFeatureDialogStyles,
    CreateFeatureMenuItem,
} from './create-feature-dialog-helper';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface CreateFeatureViewProps {
    selectedOption: string;
    handleClose: () => void;
    handleNext: () => void;
    displayPermissionDialog: boolean;
    setSelectedOption: (option: string) => void;
    handlePermissionDialogClose: () => void;
    items: CreateFeatureMenuItem[];
    headingText: string;
    menuItemStyles?: any;
    listItemStyles?: any;
    isAccordionView?: boolean;
    accordionItems?: CreateAccordionMenuItemsData[];
}
export const CreateFeatureDialog = ({
    selectedOption,
    handleClose,
    handleNext,
    setSelectedOption,
    displayPermissionDialog,
    handlePermissionDialogClose,
    items,
    headingText,
    menuItemStyles,
    listItemStyles,
    isAccordionView,
    accordionItems,
}: CreateFeatureViewProps): React.JSX.Element => {
    const [accordionItemsData, setAccordionItemsData] = useState<
        CreateAccordionMenuItemsData[]
    >(accordionItems ?? []);
    const onClickAway = (event: MouseEvent | TouchEvent): void => {
        event.preventDefault();
    };

    const handleOptionChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ): void => {
        setSelectedOption(event.target.value);
    };

    const handleAccordionChange = (heading: string): void => {
        setAccordionItemsData((prevItems) => {
            return prevItems.map((item) => {
                if (item.heading === heading) {
                    return {
                        ...item,
                        isExpanded: !item.isExpanded,
                    };
                }
                return {
                    ...item,
                    isExpanded: false,
                };
            });
        });
    };

    const renderMenuItem = (
        menuItem: CreateFeatureMenuItem
    ): React.JSX.Element => {
        return (
            <MenuItem
                sx={{
                    ...createFeatureDialogStyles.menuItemStyle,
                    ...menuItemStyles,
                }}
                className={`${
                    menuItem.navigationPath === selectedOption ? 'active' : ''
                }`}
                onClick={(): void => setSelectedOption(menuItem.navigationPath)}
            >
                <img
                    style={{
                        ...createFeatureDialogStyles.imgStyle,
                        ...menuItem.styles,
                    }}
                    src={menuItem.icon}
                    alt='ICON'
                />
                <ListItemText
                    primary={menuItem.title}
                    secondary={menuItem.subtitle}
                    sx={{
                        ...createFeatureDialogStyles.listItemStyle,
                        ...listItemStyles,
                    }}
                />
                <Radio value={menuItem.navigationPath} />
            </MenuItem>
        );
    };

    return (
        <ClickAwayListener onClickAway={onClickAway}>
            <Dialog
                sx={createFeatureDialogStyles.root}
                disableRestoreFocus
                open={true}
                closable={true}
                onClose={handleClose}
                TransitionComponent={Fade}
                TransitionProps={{ timeout: 0 }}
            >
                <Paper
                    sx={{
                        ...createFeatureDialogStyles.paperStyle,
                        mb: isAccordionView ? 0 : 3,
                    }}
                >
                    <Grid sx={createFeatureDialogStyles.gridStyle}>
                        <Typography sx={createFeatureDialogStyles.headingStyle}>
                            {headingText}
                        </Typography>
                        <form noValidate>
                            {isAccordionView ? (
                                React.Children.toArray(
                                    accordionItemsData?.map(
                                        (
                                            details: CreateAccordionMenuItemsData
                                        ): React.JSX.Element => {
                                            return (
                                                <Accordion
                                                    variant='elevation'
                                                    expanded={
                                                        details.isExpanded
                                                    }
                                                    onChange={(): void =>
                                                        handleAccordionChange(
                                                            details.heading
                                                        )
                                                    }
                                                    sx={
                                                        createFeatureDialogStyles.accordionStyles
                                                    }
                                                >
                                                    <AccordionSummary
                                                        expandIcon={
                                                            <ExpandMoreIcon />
                                                        }
                                                        header={details.heading}
                                                        sx={
                                                            createFeatureDialogStyles.accordionSummaryStyles
                                                        }
                                                    ></AccordionSummary>
                                                    <AccordionDetails>
                                                        <RadioGroup
                                                            value={
                                                                selectedOption
                                                            }
                                                            onChange={
                                                                handleOptionChange
                                                            }
                                                        >
                                                            <MenuList
                                                                sx={
                                                                    createFeatureDialogStyles.menuListStyle
                                                                }
                                                            >
                                                                {React.Children.toArray(
                                                                    details.menuItems.map(
                                                                        (
                                                                            menuItem: CreateFeatureMenuItem
                                                                        ) =>
                                                                            renderMenuItem(
                                                                                menuItem
                                                                            )
                                                                    )
                                                                )}
                                                            </MenuList>
                                                        </RadioGroup>
                                                    </AccordionDetails>
                                                </Accordion>
                                            );
                                        }
                                    )
                                )
                            ) : (
                                <RadioGroup
                                    value={selectedOption}
                                    onChange={handleOptionChange}
                                >
                                    <MenuList
                                        sx={
                                            createFeatureDialogStyles.menuListStyle
                                        }
                                    >
                                        {React.Children.toArray(
                                            items.map(
                                                (
                                                    menuItem: CreateFeatureMenuItem
                                                ) => renderMenuItem(menuItem)
                                            )
                                        )}
                                    </MenuList>
                                </RadioGroup>
                            )}
                        </form>
                        <Grid sx={createFeatureDialogStyles.buttonGridStyle}>
                            <Button
                                variant='outlined'
                                sx={createFeatureDialogStyles.backButtonStyle}
                                onClick={handleClose}
                            >
                                BACK
                            </Button>
                            <Button
                                sx={createFeatureDialogStyles.nextButtonStyle}
                                onClick={handleNext}
                            >
                                NEXT
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>
                {displayPermissionDialog && (
                    <InvalidPermissionsDialog
                        open={displayPermissionDialog}
                        onClose={handlePermissionDialogClose}
                    />
                )}
            </Dialog>
        </ClickAwayListener>
    );
};
